import { useState, useEffect, useRef, useMemo } from 'react'
import { isUserAdmin, refreshUserToken } from '../firebase/auth'
import { saveInvestmentAnalyses, getInvestmentAnalyses, getInvestmentMetadata } from '../firebase/investment'
import './Investment.css'

const PRODUCT_NAMES = [
  'AISec Training',
  'AISec Certifications',
  'AISec Books',
  'AI Security Skills Roadmap',
  'SaaS Security Score',
  'Spectra',
  'Secure Code Cards',
  'Firewalli'
]

const extractCompanyName = (url) => {
  if (!url) return 'Unknown'
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, '')
    return hostname.split('.')[0] || hostname
  } catch {
    return url
  }
}

const Investment = ({ user }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [analyses, setAnalyses] = useState([])
  const [metadata, setMetadata] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState('all')
  const [sortBy, setSortBy] = useState('best') // best, name, url
  const [expandedId, setExpandedId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState('cards') // cards | insights | matrix
  const fileInputRef = useRef(null)

  const checkAdminStatus = async () => {
    if (user) {
      setLoading(true)
      try {
        await refreshUserToken(user)
        const adminStatus = await isUserAdmin(user, true)
        setIsAdmin(adminStatus)
      } catch (error) {
        console.error('Error checking admin status:', error)
        setIsAdmin(false)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkAdminStatus()
  }, [user])

  useEffect(() => {
    const loadData = async () => {
      if (isAdmin) {
        try {
          const [analysesData, meta] = await Promise.all([
            getInvestmentAnalyses(),
            getInvestmentMetadata()
          ])
          setAnalyses(analysesData)
          setMetadata(meta)
        } catch (err) {
          console.error('Error loading investment data:', err)
        }
      }
    }
    loadData()
  }, [isAdmin])

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith('.json')) {
      setUploadError('Please select a JSON file')
      return
    }
    const reader = new FileReader()
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        const arr = Array.isArray(data) ? data : [data]
        if (arr.length === 0) {
          setUploadError('JSON file is empty')
          return
        }
        const first = arr[0]
        if (!first.overall_summary && !first.product_scores && !first.url) {
          setUploadError('Invalid format: expected overall_summary, product_scores, and url fields')
          return
        }
        setUploadError(null)
        setUploading(true)
        await saveInvestmentAnalyses(arr)
        setAnalyses(arr)
        setMetadata({ count: arr.length, uploadedAt: new Date() })
        setUploadSuccess(true)
        setTimeout(() => setUploadSuccess(false), 3000)
      } catch (err) {
        setUploadError(err.message || 'Failed to parse JSON')
      } finally {
        setUploading(false)
        e.target.value = ''
      }
    }
    reader.readAsText(file)
  }

  const filteredAndSortedAnalyses = useMemo(() => {
    let result = [...analyses]

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((a) => {
        const name = extractCompanyName(a.url).toLowerCase()
        const summary = (a.overall_summary || '').toLowerCase()
        const pitch = (a.best_pitch_angle || '').toLowerCase()
        const url = (a.url || '').toLowerCase()
        return name.includes(q) || summary.includes(q) || pitch.includes(q) || url.includes(q)
      })
    }

    if (selectedProduct !== 'all') {
      result = result.filter((a) =>
        (a.product_scores || []).some((ps) => ps.product_name === selectedProduct)
      )
    }

    if (sortBy === 'best' && selectedProduct !== 'all') {
      result.sort((a, b) => {
        const scoreA = (a.product_scores || []).find((ps) => ps.product_name === selectedProduct)?.score ?? 0
        const scoreB = (b.product_scores || []).find((ps) => ps.product_name === selectedProduct)?.score ?? 0
        return scoreB - scoreA
      })
    } else if (sortBy === 'best' && selectedProduct === 'all') {
      result.sort((a, b) => {
        const maxA = Math.max(...(a.product_scores || []).map((ps) => ps.score), 0)
        const maxB = Math.max(...(b.product_scores || []).map((ps) => ps.score), 0)
        return maxB - maxA
      })
    } else if (sortBy === 'name') {
      result.sort((a, b) =>
        extractCompanyName(a.url).localeCompare(extractCompanyName(b.url))
      )
    }

    return result
  }, [analyses, selectedProduct, sortBy, searchQuery])

  const getProductScore = (analysis, productName) => {
    return (analysis.product_scores || []).find((ps) => ps.product_name === productName)?.score ?? null
  }

  const getTopProduct = (analysis) => {
    const scores = analysis.product_scores || []
    if (scores.length === 0) return null
    return scores.reduce((best, curr) => (curr.score > best.score ? curr : best), scores[0])
  }

  // Computed stats and insights
  const stats = useMemo(() => {
    if (analyses.length === 0) return null
    const highScorers = analyses.filter((a) =>
      (a.product_scores || []).some((ps) => ps.score >= 8)
    ).length
    const strongFit = analyses.filter((a) => {
      const count = (a.product_scores || []).filter((ps) => ps.score >= 7).length
      return count >= 2
    }).length
    const productAvgs = PRODUCT_NAMES.map((pn) => {
      const scores = analyses.flatMap((a) =>
        (a.product_scores || []).filter((ps) => ps.product_name === pn).map((ps) => ps.score)
      )
      const avg = scores.length ? (scores.reduce((s, n) => s + n, 0) / scores.length).toFixed(1) : '-'
      return { product: pn, avg, count: scores.length }
    })
    return { total: analyses.length, highScorers, strongFit, productAvgs }
  }, [analyses])

  const topByProduct = useMemo(() => {
    if (analyses.length === 0) return {}
    return PRODUCT_NAMES.reduce((acc, pn) => {
      const withScores = analyses
        .map((a) => ({
          ...a,
          score: (a.product_scores || []).find((ps) => ps.product_name === pn)?.score ?? 0
        }))
        .filter((a) => a.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
      acc[pn] = withScores
      return acc
    }, {})
  }, [analyses])

  const bestOpportunities = useMemo(() => {
    if (analyses.length === 0) return []
    return analyses
      .map((a) => {
        const scores = a.product_scores || []
        const highCount = scores.filter((ps) => ps.score >= 7).length
        const avgScore = scores.length
          ? (scores.reduce((s, ps) => s + ps.score, 0) / scores.length).toFixed(1)
          : 0
        const maxScore = Math.max(...scores.map((ps) => ps.score), 0)
        return { ...a, highCount, avgScore: parseFloat(avgScore), maxScore }
      })
      .filter((a) => a.highCount >= 2)
      .sort((a, b) => b.highCount - a.highCount || b.avgScore - a.avgScore)
      .slice(0, 10)
  }, [analyses])

  if (loading) {
    return (
      <div className="investment-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="investment-container">
        <div className="investment-access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can access the Investment analysis.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="investment-container">
      <div className="investment-header">
        <h1>Investment Analysis</h1>
        <p className="investment-subtitle">
          Review and assess the best investment companies to approach for your products
        </p>
      </div>

      <div className="investment-toolbar">
        <input
          type="file"
          ref={fileInputRef}
          accept=".json"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn btn-primary investment-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : '📂 Upload JSON Analysis'}
        </button>
        {metadata && (
          <span className="investment-meta">
            {metadata.count} investors loaded
            {metadata.uploadedAt && (
              <span className="investment-meta-date">
                {' '}• Last upload: {(metadata.uploadedAt?.toDate ? metadata.uploadedAt.toDate() : metadata.uploadedAt)?.toLocaleDateString?.() ?? 'N/A'}
              </span>
            )}
          </span>
        )}
        {uploadError && <span className="investment-error">{uploadError}</span>}
        {uploadSuccess && <span className="investment-success">✓ Analysis saved successfully</span>}
      </div>

      {analyses.length > 0 && stats && (
        <>
          <div className="investment-stats-bar">
            <div className="investment-stat">
              <span className="investment-stat-value">{stats.total}</span>
              <span className="investment-stat-label">Total investors</span>
            </div>
            <div className="investment-stat">
              <span className="investment-stat-value">{stats.highScorers}</span>
              <span className="investment-stat-label">Score 8+ on any product</span>
            </div>
            <div className="investment-stat">
              <span className="investment-stat-value">{stats.strongFit}</span>
              <span className="investment-stat-label">Strong fit (7+ on 2+ products)</span>
            </div>
            <div className="investment-stat product-avgs">
              <span className="investment-stat-label">Avg score by product</span>
              <div className="investment-stat-avgs">
                {stats.productAvgs.map(({ product, avg }) => (
                  <span key={product} className="investment-avg-pill" title={product}>
                    {product.split(' ')[0]}: {avg}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="investment-view-tabs">
            <button
              type="button"
              className={`investment-tab ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              📋 Cards
            </button>
            <button
              type="button"
              className={`investment-tab ${viewMode === 'insights' ? 'active' : ''}`}
              onClick={() => setViewMode('insights')}
            >
              💡 Insights
            </button>
            <button
              type="button"
              className={`investment-tab ${viewMode === 'matrix' ? 'active' : ''}`}
              onClick={() => setViewMode('matrix')}
            >
              📊 Matrix
            </button>
          </div>

          <div className="investment-filters">
            <input
              type="text"
              placeholder="Search investors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="investment-search"
            />
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="investment-select"
            >
              <option value="all">All products</option>
              {PRODUCT_NAMES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="investment-select"
            >
              <option value="best">Sort by best fit</option>
              <option value="name">Sort by name</option>
            </select>
          </div>
        </>
      )}

      {analyses.length === 0 ? (
        <div className="investment-empty">
          <div className="investment-empty-icon">📊</div>
          <h2>No investment analysis loaded</h2>
          <p>Upload a JSON file containing investor analysis to get started.</p>
          <p className="investment-empty-hint">
            Expected format: array of objects with overall_summary, best_pitch_angle, product_scores, and url
          </p>
        </div>
      ) : viewMode === 'insights' ? (
        <div className="investment-insights">
          <div className="investment-insight-section">
            <h3>Top investors by product</h3>
            <p className="investment-insight-desc">Best 5 investors for each product — click to filter</p>
            <div className="investment-top-by-product">
              {PRODUCT_NAMES.map((pn) => (
                <div key={pn} className="investment-top-product-block">
                  <h4>{pn}</h4>
                  <ul>
                    {(topByProduct[pn] || []).map((a, i) => (
                      <li key={a.url}>
                        <button
                          type="button"
                          className="investment-top-link"
                          onClick={() => {
                            setSelectedProduct(pn)
                            setSortBy('best')
                            setViewMode('cards')
                          }}
                        >
                          {extractCompanyName(a.url)}
                        </button>
                        <span className={`investment-score-badge score-${a.score}`}>{a.score}/10</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="investment-insight-section">
            <h3>Best opportunities</h3>
            <p className="investment-insight-desc">Investors scoring 7+ on 2+ products — versatile fit</p>
            <div className="investment-best-opportunities">
              {bestOpportunities.length === 0 ? (
                <p className="investment-no-opportunities">No investors with 7+ on 2+ products</p>
              ) : bestOpportunities.map((a) => (
                <div key={a.url} className="investment-opportunity-card">
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="investment-opp-name">
                    {extractCompanyName(a.url)}
                  </a>
                  <div className="investment-opp-meta">
                    <span>{a.highCount} products at 7+</span>
                    <span>Avg: {a.avgScore}</span>
                    <span>Max: {a.maxScore}/10</span>
                  </div>
                  <button
                    type="button"
                    className="investment-opp-view"
                    onClick={() => {
                      setSearchQuery(extractCompanyName(a.url))
                      setViewMode('cards')
                    }}
                  >
                    View in cards →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : viewMode === 'matrix' ? (
        <div className="investment-matrix-container">
          <div className="investment-matrix-scroll">
            <table className="investment-matrix">
              <thead>
                <tr>
                  <th>Investor</th>
                  {PRODUCT_NAMES.map((p) => (
                    <th key={p} title={p}>{p.split(' ')[0]}</th>
                  ))}
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedAnalyses.slice(0, 50).map((analysis) => {
                  const scores = PRODUCT_NAMES.map((pn) => getProductScore(analysis, pn))
                  const maxScore = Math.max(...scores.filter((s) => s != null), 0)
                  return (
                    <tr key={analysis.url}>
                      <td>
                        <a href={analysis.url} target="_blank" rel="noopener noreferrer">
                          {extractCompanyName(analysis.url)}
                        </a>
                      </td>
                      {PRODUCT_NAMES.map((pn, i) => {
                        const s = scores[i]
                        return (
                          <td key={pn}>
                            {s != null ? (
                              <span className={`investment-matrix-cell score-${s}`}>{s}</span>
                            ) : (
                              <span className="investment-matrix-cell empty">—</span>
                            )}
                          </td>
                        )
                      })}
                      <td className="investment-matrix-max">
                        <span className={`investment-score-badge score-${maxScore}`}>{maxScore}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {filteredAndSortedAnalyses.length > 50 && (
            <p className="investment-matrix-note">Showing first 50 of {filteredAndSortedAnalyses.length} — use filters to narrow</p>
          )}
        </div>
      ) : (
        <div className="investment-grid">
          {filteredAndSortedAnalyses.map((analysis, idx) => {
            const companyName = extractCompanyName(analysis.url)
            const topProduct = getTopProduct(analysis)
            const isExpanded = expandedId === idx

            return (
              <div
                key={`${analysis.url}-${idx}`}
                className={`investment-card ${isExpanded ? 'expanded' : ''}`}
              >
                <div
                  className="investment-card-header"
                  onClick={() => setExpandedId(isExpanded ? null : idx)}
                >
                  <div className="investment-card-title-row">
                    <h3 className="investment-company-name">{companyName}</h3>
                    <a
                      href={analysis.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="investment-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Visit →
                    </a>
                  </div>
                  {topProduct && (
                    <div className="investment-top-product">
                      <span className="investment-top-label">Best fit:</span>
                      <span className="investment-top-name">{topProduct.product_name}</span>
                      <span className={`investment-score-badge score-${topProduct.score}`}>
                        {topProduct.score}/10
                      </span>
                    </div>
                  )}
                </div>

                <div className="investment-scores-row">
                  {PRODUCT_NAMES.map((pn) => {
                    const score = getProductScore(analysis, pn)
                    if (score === null) return null
                    return (
                      <div key={pn} className="investment-mini-score" title={pn}>
                        <span className="investment-mini-label">{pn.split(' ')[0]}</span>
                        <span className={`investment-mini-value score-${score}`}>{score}</span>
                      </div>
                    )
                  })}
                </div>

                {isExpanded && (
                  <div className="investment-card-detail">
                    <div className="investment-detail-section">
                      <h4>Summary</h4>
                      <p>{analysis.overall_summary}</p>
                    </div>
                    <div className="investment-detail-section">
                      <h4>Best pitch angle</h4>
                      <p className="investment-pitch">{analysis.best_pitch_angle}</p>
                    </div>
                    <div className="investment-detail-section">
                      <h4>Product scores</h4>
                      <ul className="investment-scores-list">
                        {(analysis.product_scores || []).map((ps) => (
                          <li key={ps.product_name}>
                            <span className="investment-ps-name">{ps.product_name}</span>
                            <span className={`investment-ps-score score-${ps.score}`}>
                              {ps.score}/10
                            </span>
                            <p className="investment-ps-justification">{ps.justification}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="investment-expand-btn"
                  onClick={() => setExpandedId(isExpanded ? null : idx)}
                >
                  {isExpanded ? '▲ Collapse' : '▼ View details'}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {analyses.length > 0 && filteredAndSortedAnalyses.length === 0 && (
        <div className="investment-no-results">
          No investors match your filters. Try adjusting search or product filter.
        </div>
      )}
    </div>
  )
}

export default Investment
